// Copyright (c) 2026, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import { createContext, useMemo, useState, useEffect, useRef } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { CssBaseline, ThemeProvider, createTheme } from '@mui/material';
import { PaletteMode } from '@mui/material';
import { useAuthContext } from '@asgardeo/auth-react';
import { themeSettings } from '@src/theme';
import Layout from '@src/layout/Layout';
import AssetView from '@src/view/asset';
import AuthLoadingScreen from '@src/components/AuthLoadingScreen';
import './App.scss';

export const ColorModeContext = createContext({ toggleColorMode: () => {} });

const getInitialMode = (): PaletteMode => {
  try {
    const stored = localStorage.getItem('internal-app-theme');
    if (stored === 'dark' || stored === 'light') return stored;
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    const systemTheme: PaletteMode = prefersDark ? 'dark' : 'light';
    localStorage.setItem('internal-app-theme', systemTheme);
    return systemTheme;
  } catch {
    return 'light';
  }
};

const App = () => {
  const [mode, setMode] = useState<PaletteMode>(getInitialMode);
  const { state, signIn } = useAuthContext();
  const [authError, setAuthError] = useState<Error | null>(null);
  const signInTriggered = useRef(false);

  useEffect(() => {
    if (!state.isLoading && !state.isAuthenticated && !signInTriggered.current) {
      signInTriggered.current = true;
      signIn().catch((err: Error) => {
        console.error('Asgardeo signIn failed', err);
        setAuthError(err);
        signInTriggered.current = false;
      });
    }
  }, [state.isLoading, state.isAuthenticated]); // signIn is a stable identity from auth context

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prev) => {
          const next = prev === 'dark' ? 'light' : 'dark';
          try { localStorage.setItem('internal-app-theme', next); } catch { /* ignore */ }
          return next;
        });
      },
    }),
    []
  );

  const theme = useMemo(() => createTheme(themeSettings(mode)), [mode]);

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {state.isLoading || !state.isAuthenticated ? (
          <AuthLoadingScreen
            error={authError}
            onRetry={() => {
              setAuthError(null);
              signInTriggered.current = false;
              signIn().catch((err: Error) => {
                console.error('Asgardeo signIn failed', err);
                setAuthError(err);
              });
            }}
          />
        ) : (
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<Layout />}>
                <Route index element={<AssetView />} />
              </Route>
            </Routes>
          </BrowserRouter>
        )}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
};

export default App;
